'use client'

import React, { useEffect, useState, useCallback } from 'react';
import { Box, Skeleton, useMediaQuery, Typography } from '@mui/material';
import { HomepageCategory, Venue } from '../../../shared/models';
import { getAllHomepageCategories } from '../lib/firebase/homepage_category';
import { MobileVerticalFeaturedCard, MobileVerticalFeaturedCardSkeletonGrid } from './mobile_vertical_feature_card';

// Import MUI icons for categories
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import WeekendIcon from '@mui/icons-material/Weekend';
import PeopleIcon from '@mui/icons-material/People';

// Additional imports needed
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import HomeIcon from '@mui/icons-material/Home';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import ExploreIcon from '@mui/icons-material/Explore';
import RoomIcon from '@mui/icons-material/Room';
import GroupIcon from '@mui/icons-material/Group';
import GroupsIcon from '@mui/icons-material/Groups';
import LandscapeIcon from '@mui/icons-material/Landscape';
import ParkIcon from '@mui/icons-material/Park';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DiamondIcon from '@mui/icons-material/Diamond';

interface VenuesShowcaseProps {
    attributeIcons: Record<string, string>;
    venues: Venue[];
    venuePrices?: Record<string, number> | null;
}

// Map for category icons
const CATEGORY_ICONS = {
    'trending': <TrendingUpIcon />,
    'free': <MoneyOffIcon />,
    'upscale': <DiamondIcon />,
    'charming': <HomeIcon />,
    'corporate': <BusinessCenterIcon />,
    'unique': <ExploreIcon />,
    'restaurants': <RestaurantIcon />,
    'bars': <LocalBarIcon />,
    'lounge': <WeekendIcon />,
    'entertainment': <TheaterComedyIcon />,
    'raw space': <RoomIcon />,
    'intimate': <GroupIcon />,
    'mid-size': <GroupsIcon />,
    'large': <PeopleIcon />,
    'scenic': <LandscapeIcon />,
    'outdoor': <ParkIcon />,
    '$': <AttachMoneyIcon />,
    '$$': <AttachMoneyIcon fontSize="small" sx={{ mr: -0.5 }} />,
    '$$$': <DiamondIcon />,
    'default': <ExploreIcon />
};

const VenuesShowcase: React.FC<VenuesShowcaseProps> = ({ attributeIcons, venues, venuePrices }) => {
    const [homepageCategory, setHomepageCategory] = useState<HomepageCategory | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isCategoryLoading, setIsCategoryLoading] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
    const [isScrolled, setIsScrolled] = useState(false);
    const isDesktop = useMediaQuery('(min-width:768px)');

    // Handle mounting state to prevent hydration mismatch
    useEffect(() => {
        setMounted(true);
    }, []);

    // Add scroll listener with enhanced stability
    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        let ticking = false;

        const handleScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    // Clear any pending timeout
                    clearTimeout(timeoutId);

                    // Use a short debounce to prevent excessive state changes
                    timeoutId = setTimeout(() => {
                        const scrollY = window.scrollY;

                        // Implement hysteresis with wider threshold gap to prevent layout feedback loops
                        // Use larger thresholds to be more resistant to small layout changes
                        if (!isScrolled && scrollY > 30) {
                            // Enter scrolled state when scrolling down past 30px
                            setIsScrolled(true);
                        } else if (isScrolled && scrollY < 10) {
                            // Exit scrolled state when scrolling up past 10px
                            setIsScrolled(false);
                        }
                    }, 16); // One frame delay to smooth out transitions

                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(timeoutId);
        };
    }, [isScrolled]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const categories = await getAllHomepageCategories();
                const nyCategory = categories.find(cat => cat.ID === 'New York');
                if (nyCategory) {
                    nyCategory.CategoriesAndVenuesIDsInOrder.forEach(category => {
                        category.VenueIDs = category.VenueIDs.sort(() => Math.random() - 0.5);
                    });
                    setHomepageCategory(nyCategory);
                }
            } catch (error) {
                console.error('Error fetching categories:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCategories();
    }, []);

    const handleCategoryChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
        setSelectedCategoryIndex(newValue);
    }, []);

    // Show loading state when changing categories
    useEffect(() => {
        if (homepageCategory) {
            setIsCategoryLoading(true);
            // Use requestAnimationFrame to switch the loading state after the next paint
            // This allows the animation to start immediately without artificial delays
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setIsCategoryLoading(false);
                });
            });
        }
    }, [selectedCategoryIndex]);

    // Get the venues for the selected category
    const selectedCategoryVenues = React.useMemo(() => {
        if (!homepageCategory || homepageCategory.CategoriesAndVenuesIDsInOrder.length === 0) {
            return [];
        }

        const categoryVenueIds = homepageCategory.CategoriesAndVenuesIDsInOrder[selectedCategoryIndex]?.VenueIDs || [];
        // Get venues in this category and randomize their order
        return venues
            .filter(venue => categoryVenueIds.includes(venue.ID))
            .sort(() => Math.random() - 0.5);
    }, [homepageCategory, venues, selectedCategoryIndex]);

    const getIconForCategory = (categoryTitle: string) => {
        const lowerCaseTitle = categoryTitle.toLowerCase();
        return CATEGORY_ICONS[lowerCaseTitle] || CATEGORY_ICONS.default;
    };

    if (!mounted) {
        return (
            <Box sx={{ width: '100%' }}>
                <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={400}
                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
                />
            </Box>
        );
    }

    if (!homepageCategory || isLoading) {
        return (
            <Box sx={{ width: '100%', mb: 6, backgroundColor: '#1C1C34' }}>
                {/* Category tabs skeleton - matches sticky tabs design */}
                <Box sx={{
                    position: 'sticky',
                    top: { xs: 63, md: 64 },
                    zIndex: 40,
                    backgroundColor: '#1C1C34',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    paddingTop: { xs: '12px', md: '24px' },
                    transition: 'padding 0.3s ease',
                    transform: 'translateZ(0)',
                    backfaceVisibility: 'hidden',
                }}>
                    <Box sx={{
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        WebkitOverflowScrolling: 'touch',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        '&::-webkit-scrollbar': { display: 'none' },
                        py: { xs: 2, md: 4 },
                        pl: 3,
                    }}>
                        {[...Array(14)].map((_, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: 'inline-flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mr: { xs: 3, md: 4 },
                                    opacity: index === 0 ? 1 : 0.7,
                                }}
                            >
                                <Skeleton
                                    variant="circular"
                                    width={56}
                                    height={56}
                                    animation="wave"
                                    sx={{
                                        bgcolor: 'rgba(255, 255, 255, 0.08)',
                                        mb: 1,
                                        border: index === 0 ? '1px solid rgba(224, 177, 241, 0.3)' : 'none',
                                        backgroundColor: index === 0 ? 'rgba(224, 177, 241, 0.15)' : 'rgba(255, 255, 255, 0.08)',
                                    }}
                                />
                                <Skeleton
                                    variant="text"
                                    width={70}
                                    height={16}
                                    animation="wave"
                                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.08)' }}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Content area with top padding to match loaded state */}
                <Box sx={{
                    pt: { xs: 10 },
                    px: 0
                }}>
                    {/* Venue cards skeleton grid - matches grid layout */}
                    <div className="px-3">
                        <MobileVerticalFeaturedCardSkeletonGrid count={8} />
                    </div>
                </Box>
            </Box>
        );
    }

    return (
        <div className="w-full" style={{ backgroundColor: '#1C1C34' }}>
            {/* Sticky category tabs, redesigned for better legibility */}
            <Box sx={{
                position: 'sticky',
                top: { xs: 63, md: 64 }, // Slight adjustment for mobile to prevent gap
                zIndex: 40,
                backgroundColor: '#1C1C34',
                borderTop: 'none',
                borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                marginTop: 0,
                height: 'auto', // Let content determine height naturally
                minHeight: { xs: '100px', md: '120px' }, // Minimum height to prevent layout shift
                paddingTop: { xs: '12px', md: isScrolled ? '16px' : '24px' }, // Slightly reduce top padding when scrolled
                paddingBottom: { xs: '12px', md: '16px' }, // Add bottom padding for breathing room
                transition: 'padding 0.3s ease', // Smooth transition for padding changes
                transform: 'translateZ(0)', // Force hardware acceleration
                backfaceVisibility: 'hidden', // Prevent flickering on some mobile browsers
                overflow: 'visible', // Ensure content isn't clipped
            }}>
                {/* Text-based category navigation for better legibility */}
                <Box sx={{
                    overflowX: 'auto',
                    overflowY: 'visible',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                    py: { xs: 1, md: 1.5 }, // Consistent padding regardless of scroll state
                    pl: 3,
                    // Remove scaling transformation completely to prevent cut-off
                    transform: 'none',
                    transition: 'none', // Remove transition to prevent layout issues
                    height: 'auto',
                    width: '100%', // Ensure full width
                }}>
                    {homepageCategory.CategoriesAndVenuesIDsInOrder.map((category, index) => (
                        <Box
                            key={index}
                            onClick={() => setSelectedCategoryIndex(index)}
                            sx={{
                                display: 'inline-flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mr: { xs: 3, md: 4 },
                                opacity: selectedCategoryIndex === index ? 1 : 0.7,
                                transition: 'opacity 0.2s ease',
                                cursor: 'pointer',
                                '&:hover': {
                                    opacity: 1,
                                },
                            }}
                        >
                            {/* Highlight selected category with a circle background */}
                            <Box sx={{
                                width: 56, // Fixed size - no longer changes
                                height: 56, // Fixed size - no longer changes
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 1, // Fixed margin - no longer changes
                                backgroundColor: selectedCategoryIndex === index ? 'rgba(224, 177, 241, 0.15)' : 'transparent',
                                border: selectedCategoryIndex === index ? '1px solid rgba(224, 177, 241, 0.3)' : 'none',
                                transition: 'all 0.2s ease',
                            }}>
                                <Box sx={{
                                    color: selectedCategoryIndex === index ? '#C0C0DD' : 'white',
                                    fontSize: '1.8rem', // Fixed size - no longer changes
                                    transition: 'color 0.2s ease',
                                }}>
                                    {getIconForCategory(category.Title)}
                                </Box>
                            </Box>

                            {/* Category name with better contrast */}
                            <Typography
                                variant="body2"
                                sx={{
                                    fontFamily: "'Inter', sans-serif",
                                    fontWeight: selectedCategoryIndex === index ? 600 : 400,
                                    fontSize: '0.85rem', // Fixed size - no longer changes
                                    color: selectedCategoryIndex === index ? '#C0C0DD' : 'rgba(255, 255, 255, 0.8)',
                                    textAlign: 'center',
                                    lineHeight: 1.2,
                                    letterSpacing: selectedCategoryIndex === index ? '0.02em' : 'normal',
                                    maxWidth: 100,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                {category.Title}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Content area with top padding to account for sticky header */}
            <Box sx={{
                pt: { xs: 10 },
                px: 0
            }}>
                {/* Show skeleton when changing categories */}
                {isCategoryLoading ? (
                    <div className="px-3">
                        <MobileVerticalFeaturedCardSkeletonGrid count={8} />
                    </div>
                ) : (
                    <>
                        {/* Venue cards in a grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-6 px-3">
                            {selectedCategoryVenues.map((venue) => (
                                <MobileVerticalFeaturedCard
                                    key={venue.ID}
                                    venue={venue}
                                    venueBookedTimeSlots={{}}
                                    onHover={() => { }}
                                    people={10}
                                    venuePrices={venuePrices}
                                    currentDate={new Date()}
                                    startTime="19:00"
                                />
                            ))}
                        </div>

                        {selectedCategoryVenues.length === 0 && (
                            <Typography variant="body1" sx={{
                                textAlign: 'center',
                                mt: 6,
                                mb: 6,
                                color: 'var(--text-gray)',
                                fontFamily: "'Inter', sans-serif"
                            }}>
                                No venues available in this category
                            </Typography>
                        )}
                    </>
                )}
            </Box>
        </div>
    );
};

export default VenuesShowcase;
